import { useEffect, useRef } from "react";
import {
  useCentraContext,
  useCentraReducerDispatch,
  useCentraSelectionResponse,
} from "@made-people/grease-centra";
import { useCartState } from "@made-people/grease-cart";
import { CentraShippingMethod } from "@made-people/centra-checkout-api";
import { isAllCartItemsInWarehouse } from "../utils/is-all-cart-items-in-warehouse";
import { useAllocationRule } from "../services/centra/allocation-rule/mm-allocation-rule-provider";
import { useCartWarehouseStock } from "./use-cart-warehouse-stock";
import { useDeliveryTimeSettings } from "./use-delivery-time-settings";
import { WAREHOUSE_NAMES } from "../services/centra/allocation-rule/constants";
import { useDeliveryDataStatus } from "./use-delivery-data-status";

interface ExpressAvailability {
  isReady: boolean;
  expressAvailable: boolean;
}

const useExpressAvailability = (): ExpressAvailability => {
  const { cart } = useCartState();
  const { allocationRule } = useAllocationRule();
  const { stockByVariant } = useCartWarehouseStock();
  const deliveryTimeSettings = useDeliveryTimeSettings();

  const isReady = useDeliveryDataStatus() === "ready";

  const expressEnabledByWarehouse: Record<string, boolean> = {
    [WAREHOUSE_NAMES.LIT]: deliveryTimeSettings?.expressEnabledLit ?? true,
    [WAREHOUSE_NAMES.LOGENT]:
      deliveryTimeSettings?.expressEnabledLogent ?? false,
  };

  const prio1Warehouse = allocationRule?.warehouses[0];

  const expressAvailable = prio1Warehouse
    ? (expressEnabledByWarehouse[prio1Warehouse.name] ?? false) &&
      isAllCartItemsInWarehouse(
        cart?.items ?? [],
        String(prio1Warehouse.id),
        stockByVariant,
      )
    : false;

  return { isReady, expressAvailable };
};

export const useAutoSwitchFromExpressShipping = () => {
  const selectionResponse = useCentraSelectionResponse();
  const { checkoutAPIClient } = useCentraContext();
  const dispatch = useCentraReducerDispatch();
  const switchingRef = useRef(false);
  const { isReady, expressAvailable } = useExpressAvailability();

  const shippingMethods = selectionResponse?.shippingMethods ?? [];
  const selectedMethod = selectionResponse?.selection?.shippingMethod;
  const expressIsSelected = selectedMethod === "EXPRESS";
  const shouldSwitch = isReady && !expressAvailable && expressIsSelected;

  const fallbackMethod = shippingMethods.find(
    (m) => m.shippingMethod !== "EXPRESS",
  )?.shippingMethod;

  useEffect(() => {
    if (!shouldSwitch || !fallbackMethod || switchingRef.current) {
      return;
    }

    switchingRef.current = true;
    checkoutAPIClient
      .setShippingMethod({ shippingMethod: fallbackMethod })
      .then(({ success }) => {
        if (success) {
          dispatch({
            type: "SET_SELECTION_RESPONSE",
            payload: { selectionResponse: success },
          });
        }
      })
      .finally(() => {
        switchingRef.current = false;
      });
  }, [shouldSwitch, fallbackMethod]);
};

export const useExpressShippingFilter = (): CentraShippingMethod[] => {
  const selectionResponse = useCentraSelectionResponse();
  const { isReady, expressAvailable } = useExpressAvailability();

  // Only narrow Centra's list when the warehouse data is trustworthy. While it
  // loads the list is not rendered at all, and on error we defer to Centra
  // rather than hiding a method the customer may be entitled to.
  const showExpress = isReady ? expressAvailable : true;

  return (selectionResponse?.shippingMethods ?? []).filter(
    (method) => showExpress || method.shippingMethod !== "EXPRESS",
  );
};
